export function countDecimalPlaces(value: number | string): number {
  const [mantissa, exponent] = String(value).split(/[eE]/);
  const decimals = mantissa.split('.')[1]?.length ?? 0;

  // a malformed exponent counts as no exponent
  return exponent && /^[+-]?\d+$/.test(exponent) ? Math.max(0, decimals - Number(exponent)) : decimals;
}

const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;
const EXPONENT_FORM = /^(-?)(\d+)(?:\.(\d*))?e([+-]?\d+)$/i;
const MAX_EXPONENT = 400;

export function toDecimalString(value: number | string): string | null {
  const text = typeof value === 'string' ? value.trim().replace(/^\+/, '') : String(value);
  if (PLAIN_DECIMAL.test(text)) return text;

  const match = EXPONENT_FORM.exec(text);
  if (!match) {
    const number = Number(text);
    return typeof value === 'string' && Number.isFinite(number) ? toDecimalString(number) : null;
  }

  const [, sign, lead, rest = '', exponent] = match;
  const digits = lead + rest;
  const point = lead.length + Number(exponent);
  if (Math.abs(point) > MAX_EXPONENT) return null;
  if (point <= 0) return `${sign}0.${'0'.repeat(-point)}${digits}`;
  if (point >= digits.length) return sign + digits + '0'.repeat(point - digits.length);
  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
}

export function addDecimalStrings(a: string, b: string): string {
  const scale = Math.max(countDecimalPlaces(a), countDecimalPlaces(b));
  const sum = toScaledInteger(a, scale) + toScaledInteger(b, scale);

  const digits = (sum < 0n ? -sum : sum).toString().padStart(scale + 1, '0');
  const integer = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale).replace(/0+$/, '');
  return `${sum < 0n ? '-' : ''}${integer}${fraction ? `.${fraction}` : ''}`;
}

function toScaledInteger(value: string, scale: number): bigint {
  const [integer, fraction = ''] = value.replace('-', '').split('.');
  const scaled = BigInt(integer + fraction.padEnd(scale, '0'));
  return value.startsWith('-') ? -scaled : scaled;
}
